import React from "react";
import { Button, ButtonProps } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";

/**
 * PM5 T1 (chrome, batch "common") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) `sx`. See docs/mui-conversion-guide.md.
 *
 * Shared button primitives (login / signup SMS flows). Later tranches build on
 * these, so the bar is that the emitted CSS is unchanged: every key below is
 * the v4 rule it replaces. All three targets are MUI `Button` instances styled
 * a few literal times, so guide §1.2 row 3 applies — local `sx` constants.
 *
 * `theme.spacing(n)` becomes the bare `sx` spacing multiple (`marginBottom: 2`
 * = 16px on this theme's 8px unit) — same computed value, no theme callback
 * needed. `backgroundColor: theme.palette.primary.main` becomes the `sx`
 * palette path `"primary.main"`. `borderWidth` is not an `sx` style prop, so it
 * passes through to emotion, which appends `px` to the number exactly as JSS's
 * default-unit plugin did in v4.
 *
 * NOTE on Button colour: unlike the plain `<Button>`s elsewhere in this batch,
 * none of these needs the guide §2 "Button default color" parity restore —
 * each one pins its own `color` (white/black) in the rules below, so v5+'s
 * change of the default from `default` to `primary` cannot reach the label.
 */

// was: useStyles().whatsappButton
const whatsappButtonSx = {
  marginBottom: 2,
  borderWidth: 2,
  backgroundColor: "#25D366",
  borderColor: "black",
  color: "white",
  "&:hover": {
    backgroundColor: "#25D366",
    borderColor: "white",
    color: "white",
  },
};

// was: useStyles().smsButton
const smsButtonSx = {
  marginBottom: 2,
  borderWidth: 2,
  backgroundColor: "primary.main",
  borderColor: "black",
  color: "black",
  "&:hover": {
    backgroundColor: "primary.main",
    borderColor: "white",
    color: "black",
  },
};

// was: useStyles().secondaryButton
const secondaryButtonSx = {
  marginBottom: 1,
  borderWidth: 2,
  borderColor: "black",
  color: "white",
  "&:hover": {
    borderWidth: 2,
    marginBottom: 1,
    borderColor: "white",
    color: "white",
  },
  "&:disabled": {
    borderWidth: 2,
    marginBottom: 1,
    borderColor: "rgba(255,255,255,0.8)",
    color: "rgba(255,255,255,0.8)",
  },
};

type CustomButtonProps = Omit<ButtonProps, "children"> & { title: string };

export const WhatsappButton = ({ title, ...props }: CustomButtonProps) => {
  return (
    <Button
      fullWidth={true}
      startIcon={<WhatsAppIcon />}
      sx={whatsappButtonSx}
      {...props}
    >
      {title || ""}
    </Button>
  );
};

export const SmsButton = ({ title, ...props }: CustomButtonProps) => {
  return (
    <Button
      fullWidth={true}
      startIcon={<SmsIcon />}
      sx={smsButtonSx}
      color={"secondary"}
      {...props}
    >
      {title || ""}
    </Button>
  );
};

export const SmsButtonSecondary = ({ title, ...props }: CustomButtonProps) => {
  return (
    <Button
      startIcon={<SmsIcon />}
      variant="outlined"
      fullWidth={true}
      sx={secondaryButtonSx}
      color={"secondary"}
      {...props}
    >
      {title || ""}
    </Button>
  );
};

export const SecondaryButton = ({ title, ...props }: CustomButtonProps) => {
  return (
    <Button
      variant="outlined"
      fullWidth={true}
      sx={secondaryButtonSx}
      color={"secondary"}
      {...props}
    >
      {title || ""}
    </Button>
  );
};

export const PrimaryButton = ({ title, ...props }: CustomButtonProps) => {
  return (
    <Button fullWidth={true} sx={smsButtonSx} color={"secondary"} {...props}>
      {title || ""}
    </Button>
  );
};
